/**
 * Комментарии к позиции сметы (СМ-09): замечания сметчика и проверяющего, пометка «решено». Цен здесь нет.
 */
import type { EstimateItemComment } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { Button } from '../../ui/button';
import { Badge, toast } from '../../ui/feedback';
import { Textarea } from '../../ui/form';

const linkCls = 'inline-flex min-h-[44px] items-center text-accent-text underline md:min-h-[28px]';

export function ItemComments({ itemId }: { itemId: string }) {
  const qc = useQueryClient();
  const key = ['estimate-item-comments', itemId];
  const q = useQuery({
    queryKey: key,
    queryFn: () => api<EstimateItemComment[]>(`/estimate-items/${itemId}/comments`),
    retry: false,
  });
  const [text, setText] = useState('');
  const done = (rows: EstimateItemComment[]) => qc.setQueryData(key, rows);
  const add = useMutation({
    mutationFn: () =>
      api<EstimateItemComment[]>(`/estimate-items/${itemId}/comments`, { body: { body: text } }),
    onSuccess: (rows) => {
      setText('');
      done(rows);
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  const act = useMutation({
    mutationFn: (v: { id: string; action: 'resolve' | 'reopen' | 'delete' }) =>
      api<EstimateItemComment[]>(`/estimate-items/${itemId}/comments/${v.id}`, {
        body: { action: v.action },
      }),
    onSuccess: done,
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  const rows = q.data ?? [];
  return (
    <div className="flex flex-col gap-2 rounded-md border border-line p-3 text-[14px]">
      <div className="font-medium">Комментарии к позиции{rows.length ? ` (${rows.length})` : ''}</div>
      {rows.length ? (
        <ul className="flex flex-col gap-2">
          {rows.map((c) => (
            <li key={c.id} className="flex flex-col gap-1">
              <div className="flex flex-wrap items-center gap-2 text-ink-2">
                <span>{c.author ?? 'Сотрудник'}</span>
                <span>{dateTime(c.createdAt)}</span>
                {c.resolved ? (
                  <Badge tone="success">решено{c.resolvedBy ? `: ${c.resolvedBy}` : ''}</Badge>
                ) : null}
              </div>
              <div className={c.resolved ? 'text-ink-2' : undefined}>{c.body}</div>
              {c.canManage ? (
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    className={linkCls}
                    disabled={act.isPending}
                    onClick={() => act.mutate({ id: c.id, action: c.resolved ? 'reopen' : 'resolve' })}
                  >
                    {c.resolved ? 'вернуть в работу' : 'решено'}
                  </button>
                  <button
                    type="button"
                    className={linkCls}
                    disabled={act.isPending}
                    onClick={() => act.mutate({ id: c.id, action: 'delete' })}
                  >
                    убрать
                  </button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-ink-2">Комментариев нет</div>
      )}
      <Textarea
        value={text}
        maxLength={2000}
        rows={2}
        placeholder="Написать замечание или пояснение к позиции"
        onChange={(e) => setText(e.target.value)}
      />
      <div>
        <Button
          className="min-h-[44px] md:min-h-0"
          variant="secondary"
          loading={add.isPending}
          disabled={!text.trim()}
          onClick={() => add.mutate()}
        >
          Добавить комментарий
        </Button>
      </div>
    </div>
  );
}
